/**
 * Student Availability service — Firestore CRUD for `studentAvailability`.
 *
 * A document defines one recurring weekly "I'm generally free" window:
 *   {
 *     studentId, studentEmail,      // owner (email denormalized for rules)
 *     mentorIds, mentorEmails,      // every mentor this student learns with,
 *                                   // mirrored from students/{id} so each of
 *                                   // them can read these windows (the mentor
 *                                   // queries by studentId, so the rule has to
 *                                   // match on the doc — see functions mentorLinks)
 *     mentorId,                     // derived primary mentor (legacy readers)
 *     dayOfWeek: 0-6,               // 0 = Sunday … 6 = Saturday
 *     startMinutes, endMinutes,     // minutes from midnight, wall-clock in `timezone`
 *     timezone,                     // IANA-labelled tz, e.g. "Asia/Kolkata (IST)"
 *     active: boolean,
 *     createdAt, updatedAt
 *   }
 *
 * These windows are only consulted when a MENTOR proposes a reschedule — the
 * callable validates the proposed time against them. Mirror of
 * mentorAvailability.service.js.
 */
import firestoreService, { where } from './firestore.service';

const COLLECTION = 'studentAvailability';

function sanitize(input = {}) {
  return {
    studentId: input.studentId || '',
    studentEmail: (input.studentEmail || '').trim().toLowerCase(),
    mentorIds: Array.isArray(input.mentorIds) ? input.mentorIds.filter(Boolean) : [],
    mentorEmails: Array.isArray(input.mentorEmails)
      ? input.mentorEmails.map((e) => (e || '').trim().toLowerCase()).filter(Boolean)
      : [],
    mentorId: input.mentorId || '',
    dayOfWeek: Number(input.dayOfWeek),
    startMinutes: Number(input.startMinutes),
    endMinutes: Number(input.endMinutes),
    timezone: input.timezone || '',
    active: input.active !== false,
  };
}

/** Sort by weekday then start time so lists/calendars read top-to-bottom. */
function sortWindows(rows) {
  return [...rows].sort(
    (a, b) => a.dayOfWeek - b.dayOfWeek || a.startMinutes - b.startMinutes
  );
}

export const studentAvailabilityService = {
  /** Realtime subscription to one student's availability windows. */
  subscribeByStudent(studentId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('studentId', '==', studentId)],
      (rows) => onData(sortWindows(rows)),
      onError
    );
  },

  async listByStudent(studentId) {
    const rows = await firestoreService.getCollection(COLLECTION, [
      where('studentId', '==', studentId),
    ]);
    return sortWindows(rows);
  },

  create(data) {
    return firestoreService.addDocument(COLLECTION, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(COLLECTION, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(COLLECTION, id);
  },
};

export default studentAvailabilityService;
